<template>
  <div class="relative flex h-[52px] items-center">
    <template v-if="props.isCurrentMonth">
      <div v-if="showRangeBg" class="absolute top-1/2 h-9 -translate-y-1/2 bg-[#F4F1ED]" :class="rangeBgPositionClass" />
      <button
        class="relative z-10 flex w-full items-center justify-center py-2 font-Spectral text-[16px] md:text-[17px]"
        type="button"
        :aria-label="ariaLabel"
        :aria-pressed="isEdge"
        :disabled="isDisabled"
        @click="props.onClick(date)"
      >
        <span :class="dayIndicatorClass">{{ props.day.date() }}</span>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs';

interface Props {
  /** UTC モードの暦日なので、format() の結果がそのまま JST の日付になる */
  day: Dayjs;
  isCurrentMonth: boolean;
  from: string;
  to: string;
  minDate: string;
  onClick: (date: string) => void;
}

const props = defineProps<Props>();

const date = computed(() => props.day.format('YYYY-MM-DD'));
const ariaLabel = computed(() => props.day.locale('en').format('MMMM D, YYYY'));

const isDisabled = computed(() => date.value < props.minDate);
const isStart = computed(() => date.value === props.from);
const isEnd = computed(() => date.value === props.to);
const isEdge = computed(() => !isDisabled.value && (isStart.value || isEnd.value));
const hasRange = computed(() => props.from !== '' && props.to !== '' && props.from !== props.to);
const isInRange = computed(() => hasRange.value && date.value > props.from && date.value < props.to);

const showRangeBg = computed(() => hasRange.value && (isStart.value || isEnd.value || isInRange.value));

const rangeBgPositionClass = computed(() => {
  if (isStart.value) return 'left-1/2 right-0';
  if (isEnd.value) return 'left-0 right-1/2';
  return 'left-0 right-0';
});

const dayIndicatorClass = computed(() => {
  if (isEdge.value) {
    return 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-gold3 text-white';
  }
  if (isDisabled.value) {
    return 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-gray-400 opacity-60';
  }
  return 'inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-sm text-gray9 hover:bg-black/5';
});
</script>
